import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import AuthScreen from '../components/AuthScreen';
import LoginForm from '../components/LoginForm';
import { useAuth } from '../auth/AuthContext';

/**
 * 登入頁（主 app；＝首頁 `/`）。
 * ⚠️ 表單**同理事會端共用**（`components/LoginForm`）—— 同一個樣、同一套欄位。
 * ⚠️ `?t=` ＝ WhatsApp QR 登記回覆嘅一次性登入連結（自動登入，唔顯示表單）。
 * ⚠️ `?next=` ＝ 登入後去邊（例：`/login?next=/committee`）；預設會員區。
 */
export default function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, loading, loginWithToken } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const next = params.get('next') ?? '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setBusy(true);
    void loginWithToken(token).then((r) => {
      if (!alive) return;
      setBusy(false);
      if (r.ok) nav(next && next.startsWith('/') ? next : '/member', { replace: true });
      else setError(t('token_invalid'));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * 認證外殼（共用 `AuthScreen`）：卡片置中**上移 20px** ＋ 法務連結貼螢幕最底
   * ＋ 頂欄**冇標題**（只有 logo）—— 產品負責人 2026-10-06 指定。
   */
  if (token) {
    return (
      <AuthScreen>
        <div className="text-center" style={{ color: 'var(--color-text-secondary)' }}>
          {busy ? t('token_working') : error || t('token_invalid')}
        </div>
      </AuthScreen>
    );
  }

  /**
   * ⭐ **已登入就唔應該再見到登入頁**（產品負責人 2026-10-06：「登入後保持登入 30 日」）。
   *
   * 為何一定要做：PWA 嘅 `start_url` 係 `/` → **每次開 app 都入呢頁**。
   *   舊版唔檢查 session → 明明 cookie 未過期（30 日）都照顯示登入表，
   *   用戶就會以為「每次都叫我登入」。呢個係**顯示問題**，唔係 session 過期。
   *
   *  · `loading`（`/api/auth/me` 查緊）→ 暫時留白，**唔好閃個登入表出嚟**
   *  · 已登入 → 直接入 app（職員 → `/admin`；會員 → `/member`；有 `?next=` 就跟）
   * ⚠️ `?t=`（一次性登入連結）優先處理，唔會被呢段蓋過。
   */
  if (!token && loading) {
    return (
      <AuthScreen>
        <div />
      </AuthScreen>
    );
  }

  if (!token && user) {
    const staff = user.role === 'ai85' || user.role === 'staff';
    const dest = next && next.startsWith('/') ? next : staff ? '/admin' : '/member';
    return <Navigate to={dest} replace />;
  }

  return (
    <AuthScreen>
      <LoginForm onSuccess={() => nav(next && next.startsWith('/') ? next : '/member')} />
    </AuthScreen>
  );
}
